import { useState } from "react";
import { useRule } from "../CreateRuleNew/RuleReducer";
import Button2 from "../../../Button/Button2";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Divider } from "primereact/divider";
import { checkSiteExists, PowerSearchAPI } from "../../../../../Api Methods/Api";
import { AutoComplete } from "primereact/autocomplete";
import { useDispatch, useSelector } from "react-redux";
import classes from "./RuleWizard.module.css";
import Button3 from "../../../Button/Button3";

const WizardStepActions = ({ pins, smartLocks, onNext, onBack }) => {
    const { state, dispatch } = useRule();
    const token = useSelector(s => s.auth.token);
    const client = useSelector(s => s.auth.client);
    const _dispatch = useDispatch();

    const [linkedSites, setLinkedSites] = useState([]);
    const [linkSiteVisible, setLinkSiteVisible] = useState(false);
    const [siteSearchTerm, setSiteSearchTerm] = useState("");
    const [siteSuggestions, setSiteSuggestions] = useState([]);
    const [siteError, setSiteError] = useState("");
    const [showTimeDialog, setShowTimeDialog] = useState(false);
    const [showDelayDialog, setShowDelayDialog] = useState(false);
    const [timeLength, setTimeLength] = useState(0);
    const [timeUnit, setTimeUnit] = useState("minutes");
    const [delayLength, setDelayLength] = useState(0);
    const [pendingSelection, setPendingSelection] = useState(null);
    const [delayTarget, setDelayTarget] = useState(null);
    const [message, setMessage] = useState("");

    const filteredPins = pins
        .filter(pin =>
            pin.type !== "INPUT" &&
            pin.type !== "VIRTUAL_INPUT" &&
            !smartLocks.some(lock => parseInt(lock.outputPin) === pin.Pin) &&
            pin.Pin !== state.pinNumber
        )
        .map(pin => ({ ...pin, DeviceUUID: state.deviceUUID || pin.DeviceUUID }));

    const allDisplaySites = [
        { key: "This site", pins: filteredPins },
        ...linkedSites.map(site => ({ key: site.siteName || site.uuid, pins: site.pins })),
    ];

    const isPinSelected = (pin) =>
        state.selectedPinsToAffect.some(p => p.Pin === pin.Pin && p.DeviceUUID === pin.DeviceUUID);

    const handlePinToggle = (pin) => {
        const isSelected = isPinSelected(pin);
        const action = {
            site_to: pin.DeviceUUID,
            action_type: "CONTROL_OUTPUT",
            execution_order: state.actions.filter(a => a.action_type === "CONTROL_OUTPUT").length + 1,
            delay_seconds: 0,
            control_type: "",
            target_output: String(pin.Pin),
        };
        dispatch({
            type: "SET_ADV_RULE_ACTION",
            payload: isSelected
                ? state.actions.filter(a => !(a.site_to === action.site_to && a.target_output === action.target_output && a.action_type === "CONTROL_OUTPUT"))
                : [...state.actions.filter(a => !(a.site_to === action.site_to && a.target_output === action.target_output)), action],
        });
        dispatch({
            type: "SET_SELECTED_PINS_TO_AFFECT",
            payload: isSelected
                ? state.selectedPinsToAffect.filter(p => !(p.Pin === pin.Pin && p.DeviceUUID === pin.DeviceUUID))
                : [...state.selectedPinsToAffect, pin],
        });
    };

    const outputStateSelected = (type, pin) => {
        if (type === 1 || type === 3) {
            setPendingSelection({ type, pin });
            setShowTimeDialog(true);
            return;
        }
        applyOutputSelection(type, pin, null);
    };

    const applyOutputSelection = (type, pin, timeLengthMs) => {
        const controlTypes = ["ARM", "TEMP_ARM", "DISARM", "TEMP_DISARM", "OPPOSE", "TOGGLE", "PULSE", "MATCH"];
        const controlType = controlTypes[type] ?? "";
        dispatch({
            type: "SET_ADV_RULE_ACTION",
            payload: state.actions.map(a =>
                a.site_to === pin.DeviceUUID && a.target_output === String(pin.Pin)
                    ? { ...a, control_type: controlType, temporary_time_ms: (type === 1 || type === 3) ? timeLengthMs : 0 }
                    : a
            ),
        });
    };

    const handleTempConfirm = () => {
        if (!pendingSelection) return;
        const ms = Number(timeLength) * (timeUnit === "minutes" ? 60000 : 1000);
        applyOutputSelection(pendingSelection.type, pendingSelection.pin, ms);
        setPendingSelection(null);
        setShowTimeDialog(false);
        setTimeLength(0);
        setTimeUnit("minutes");
    };

    const handleSetDelay = () => {
        if (!delayTarget) return;
        dispatch({
            type: "SET_ADV_RULE_ACTION",
            payload: state.actions.map(a =>
                a.site_to === delayTarget.site_to && a.target_output === delayTarget.target_output
                    ? { ...a, delay_seconds: Number(delayLength) }
                    : a
            ),
        });
        setShowDelayDialog(false);
        setDelayTarget(null);
    };

    const getSiteKey = () => {
        if (typeof siteSearchTerm === "object" && siteSearchTerm !== null) return siteSearchTerm.key;
        return siteSearchTerm;
    };

    const searchSites = async (event) => {
        const responseData = await PowerSearchAPI({ token, client }, event.query, 50, 1, 0, 0, 0, 0, _dispatch);
        const devices = responseData?.[0]?.devices ?? [];
        setSiteSuggestions(devices.map(site => ({ key: site.key, tag: site.tag })));
    };

    const siteItemTemplate = (item) => (
        <div style={{ display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
            <span style={{ fontWeight: 600 }}>{item.key}</span>
            {item.tag && <span style={{ color: "#6c757d", fontSize: "13px" }}>{item.tag}</span>}
        </div>
    );

    const checkSite = async () => {
        setSiteError("");
        const siteKey = getSiteKey();
        const response = await checkSiteExists({ token, client }, siteKey, _dispatch);
        if (response.data?.error) {
            setSiteError("No site found, please check the Site ID and try again.");
        } else if (response.data?.device) {
            const site = response.data.device;
            const deviceDetails = JSON.parse(site.deviceDetails);
            const pinsArray = Object.entries(deviceDetails)
                .filter(([, p]) => p && typeof p === "object")
                .map(([num, p]) => ({ ...p, Pin: Number(num), DeviceUUID: site.uuid }))
                .filter(p => p.type !== "INPUT" && p.type !== "VIRTUAL_INPUT");
            setLinkedSites(prev => [...prev, { ...site, siteName: site.name || site.uuid, pins: pinsArray }]);
            setLinkSiteVisible(false);
            setSiteSearchTerm("");
            dispatch({ type: "SET_IS_ADV_RULE", payload: true });
        }
    };

    const handleNext = () => {
        if (state.selectedPinsToAffect.length === 0) {
            setMessage("Please select at least one output.");
            return;
        }
        const incomplete = state.selectedPinsToAffect.filter(pin => {
            const action = getAction(pin);
            return !action || !action.control_type;
        });
        if (incomplete.length > 0) {
            setMessage("Please choose an action for all selected outputs before continuing.");
            return;
        }
        setMessage("");
        onNext();
    };

    const getSelectedType = (pin) => {
        const action = state.actions.find(a => a.site_to === pin.DeviceUUID && a.target_output === String(pin.Pin));
        if (!action) return null;
        const map = { ARM: 0, TEMP_ARM: 1, DISARM: 2, TEMP_DISARM: 3, OPPOSE: 4, TOGGLE: 5, PULSE: 6, MATCH: 7 };
        return map[action.control_type] ?? null;
    };

    const getAction = (pin) =>
        state.actions.find(a => a.site_to === pin.DeviceUUID && a.target_output === String(pin.Pin));

    const triggerName = state.selectedRulePin?.pinName || state.pinName || "trigger";
    const isGeofence = state.isGeofence;

    const headerText = isGeofence
        ? `What happens when the ${state.geofenceDirection === "Enter" ? "first person enters" : "last person exits"} the zone?`
        : "What actions will be triggered?";

    return (
        <>
            {/* Link site dialog */}
            <Dialog
                header="Link another site"
                visible={linkSiteVisible}
                style={{ width: "38vw" }}
                onHide={() => { setLinkSiteVisible(false); setSiteError(""); setSiteSearchTerm(""); }}
            >
                {siteError && <p style={{ color: "red", fontSize: "13px" }}>{siteError}</p>}
                <AutoComplete
                    value={siteSearchTerm}
                    suggestions={siteSuggestions}
                    completeMethod={searchSites}
                    field="key"
                    itemTemplate={siteItemTemplate}
                    onChange={(e) => setSiteSearchTerm(e.value)}
                    placeholder="Search by Site ID or name"
                    appendTo="self"
                    panelStyle={{ zIndex: 11000 }}
                    style={{ width: "100%" }}
                    inputStyle={{ width: "100%" }}
                />
                <small style={{ color: "#6c757d" }}>Type a site ID or portal name to search</small>
                <br /><br />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        label="Link site"
                        severity="success"
                        icon="pi pi-check"
                        disabled={!getSiteKey()}
                        onClick={checkSite}
                    />
                </div>
            </Dialog>

            {/* Temp time dialog */}
            <Dialog
                header="For how long?"
                visible={showTimeDialog}
                style={{ width: "34vw" }}
                onHide={() => { setShowTimeDialog(false); setTimeLength(0); setTimeUnit("minutes"); }}
            >
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <InputText
                        value={timeLength}
                        onChange={e => setTimeLength(e.target.value)}
                        placeholder="Duration"
                    />
                    <div style={{ display: "flex", gap: "8px" }}>
                        {["minutes", "seconds"].map(u => (
                            <Button
                                key={u}
                                label={u.charAt(0).toUpperCase() + u.slice(1)}
                                severity={timeUnit === u ? "info" : "secondary"}
                                outlined={timeUnit !== u}
                                onClick={() => setTimeUnit(u)}
                            />
                        ))}
                    </div>
                </div>
                <br />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        label="Confirm"
                        severity="success"
                        disabled={!timeLength || Number(timeLength) === 0}
                        onClick={handleTempConfirm}
                    />
                </div>
            </Dialog>

            {/* Delay dialog */}
            <Dialog
                header="Add delay (seconds)"
                visible={showDelayDialog}
                style={{ width: "34vw" }}
                onHide={() => setShowDelayDialog(false)}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <InputText
                        value={delayLength}
                        onChange={e => setDelayLength(e.target.value)}
                        placeholder="0"
                        style={{ width: "100px" }}
                    />
                    <span style={{ color: "#6e6e6e" }}>seconds</span>
                </div>
                <br />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        label="Set delay"
                        severity="success"
                        disabled={!delayLength || Number(delayLength) === 0}
                        onClick={handleSetDelay}
                    />
                </div>
            </Dialog>

            <div className={classes.contentHeader}>
                <h2 className={classes.contentTitle}>{headerText}</h2>
                <p className={classes.contentSubtitle}>Select which switches will change state when the rule fires.</p>
            </div>

            {message && <p style={{ color: "red", margin: "0 0 8px 0", fontSize: "13px" }}>{message}</p>}

            <div className={classes.actionsLayout} style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                {/* Left: pin selector */}
                <div className={classes.actionsPinList}>
                    <p style={{ color: "#9e9e9e", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", margin: "0 0 4px 0" }}>
                        Select outputs
                    </p>
                    {allDisplaySites.map((site, si) => (
                        <div key={si}>
                            {si > 0 && (
                                <p style={{ fontSize: "11px", color: "#b0b0b0", margin: "8px 0 4px 0", fontWeight: 600 }}>
                                    {site.key}
                                </p>
                            )}
                            {site.pins.map((pin, pi) => (
                                <Button3
                                    key={pi}
                                    style={{
                                        width: "100%",
                                        minHeight: "38px",
                                        fontSize: "13px",
                                        marginBottom: "4px",
                                        backgroundColor: isPinSelected(pin) ? "var(--Primary-Color)" : undefined,
                                        color: isPinSelected(pin) ? "white" : undefined,
                                        border: isPinSelected(pin) ? "2px solid lightblue" : undefined,
                                    }}
                                    onClick={() => handlePinToggle(pin)}
                                >
                                    {pin.pinName}
                                </Button3>
                            ))}
                        </div>
                    ))}
                    <Button
                        label="Another site?"
                        size="small"
                        severity="info"
                        rounded
                        outlined
                        icon="pi pi-plus"
                        style={{ marginTop: "4px" }}
                        onClick={() => setLinkSiteVisible(true)}
                    />
                </div>

                {/* Vertical divider */}
                <Divider layout="vertical" />

                {/* Right: control config per selected pin */}
                <div className={classes.actionsConfig}>
                    {state.selectedPinsToAffect.length === 0 ? (
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#c0c0c0", fontSize: "14px" }}>
                            Select an output from the left
                        </div>
                    ) : (
                        <>
                            {state.selectedPinsToAffect.map((pin, i) => {
                                const selectedType = getSelectedType(pin);
                                const action = getAction(pin);
                                const isPulsePin = pin.type === "PULSE" || pin.type === "VIRTUAL_PULSE";

                                const isMissingAction = !action?.control_type;

                                return (
                                    <div key={i} className={classes.actionCard} style={isMissingAction ? { border: "1.5px solid #e57373", borderRadius: "8px" } : undefined}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                            {action?.delay_seconds > 0 && (
                                                <span style={{ fontSize: "12px", color: "#9e9e9e" }}>
                                                    After {action.delay_seconds}s —
                                                </span>
                                            )}
                                            <p style={{ margin: 0, fontWeight: 600, color: "#2d2d2d", fontSize: "14px" }}>
                                                {pin.pinName} will:
                                            </p>
                                        </div>

                                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                                            {!isPulsePin ? (
                                                <>
                                                    {!state.isPulse && (
                                                        <Button3
                                                            style={{
                                                                minWidth: "110px", minHeight: "34px", fontSize: "12px",
                                                                backgroundColor: selectedType === 7 ? "var(--Primary-Color)" : undefined,
                                                                color: selectedType === 7 ? "white" : undefined,
                                                                border: selectedType === 7 ? "2px solid lightblue" : undefined,
                                                            }}
                                                            onClick={() => outputStateSelected(7, pin)}
                                                        >
                                                            {`Match ${triggerName}`}
                                                        </Button3>
                                                    )}
                                                    {[
                                                        { idx: 0, label: pin.actionOneOffLabel || "Arm" },
                                                        { idx: 1, label: `Temp. ${pin.actionOneOffLabel || "Arm"}` },
                                                        { idx: 2, label: pin.actionOneOnLabel || "Disarm" },
                                                        { idx: 3, label: `Temp. ${pin.actionOneOnLabel || "Disarm"}` },
                                                        { idx: 5, label: "Toggle" },
                                                    ].map(({ idx, label }) => (
                                                        <Button3
                                                            key={idx}
                                                            style={{
                                                                minWidth: "110px", minHeight: "34px", fontSize: "12px",
                                                                backgroundColor: selectedType === idx ? "var(--Primary-Color)" : undefined,
                                                                color: selectedType === idx ? "white" : undefined,
                                                                border: selectedType === idx ? "2px solid lightblue" : undefined,
                                                            }}
                                                            onClick={() => outputStateSelected(idx, pin)}
                                                        >
                                                            {label}
                                                        </Button3>
                                                    ))}
                                                </>
                                            ) : (
                                                <Button3
                                                    style={{
                                                        minWidth: "110px", minHeight: "34px", fontSize: "12px",
                                                        backgroundColor: selectedType === 6 ? "var(--Primary-Color)" : undefined,
                                                        color: selectedType === 6 ? "white" : undefined,
                                                        border: selectedType === 6 ? "2px solid lightblue" : undefined,
                                                    }}
                                                    onClick={() => outputStateSelected(6, pin)}
                                                >
                                                    {`Temp. ${pin.actionOneOnLabel || "Disarm"}`}
                                                </Button3>
                                            )}
                                        </div>

                                        <Button
                                            label={action?.delay_seconds > 0 ? `Change delay (${action.delay_seconds}s)` : "Add delay?"}
                                            icon="pi pi-clock"
                                            size="small"
                                            severity="info"
                                            rounded
                                            outlined
                                            style={{ alignSelf: "flex-start" }}
                                            onClick={() => {
                                                setDelayTarget({ site_to: action.site_to, target_output: action.target_output });
                                                setDelayLength(action?.delay_seconds || 0);
                                                setShowDelayDialog(true);
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            </div>

            <div className={classes.navRow}>
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined onClick={onBack} />
                <Button label="Next" icon="pi pi-arrow-right" iconPos="right" severity="success" onClick={handleNext} />
            </div>
        </>
    );
};

export default WizardStepActions;
